import React, { useEffect, useState } from "react";
import Parse from "parse";
import Modal from "../primitives/Modal";
import ModalUi from "../primitives/ModalUi";
import DeleteUserConfirmation from "./DeleteUserConfirmation";

const EditUser = ({ isOpen, onClose, user, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    role: "",
    teams: []
  });
  const [originalTeams, setOriginalTeams] = useState([]);
  const [teamList, setTeamList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [delegationAction, setDelegationAction] = useState("");
  const [delegationTarget, setDelegationTarget] = useState("");
  const [hasDocuments, setHasDocuments] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [documentCount, setDocumentCount] = useState(0);

  const allRoles = ["OrgAdmin", "TeamAdmin", "Editor", "User"];

  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
      loadTeams();
      loadAllUsers();
    }
  }, [isOpen, user]);

  const loadUserData = async () => {
    try {
      setIsLoading(true);

      // Check if user has documents
      const docCheck = await checkUserHasDocuments();
      setHasDocuments(docCheck.hasDocuments);
      setDocumentCount(docCheck.count);

      const teamIds = (user.TeamIds || []).map((t) => t.objectId || t.id);

      setFormData({
        name: user.Name || "",
        phone: user.Phone || "",
        email: user.Email || "",
        password: "",
        role: user.UserRole?.replace("contracts_", "") || "",
        teams: teamIds
      });
      setOriginalTeams(teamIds);
    } catch (err) {
      console.error("Error loading user data:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const checkUserHasDocuments = async () => {
    try {
      const userId = user.UserId?.id || user.UserId?.objectId;
      if (!userId) return { hasDocuments: false, count: 0 };

      // Check as document creator
      const creatorQuery = new Parse.Query("contracts_Document");
      creatorQuery.equalTo("CreatedBy", {
        __type: "Pointer",
        className: "_User",
        objectId: userId
      });
      const creatorCount = await creatorQuery.count();

      // Check as signer
      const signerQuery = new Parse.Query("contracts_Document");
      signerQuery.equalTo("Signers.UserId", {
        __type: "Pointer",
        className: "_User",
        objectId: userId
      });
      const signerCount = await signerQuery.count();

      const totalCount = creatorCount + signerCount;
      return { hasDocuments: totalCount > 0, count: totalCount };
    } catch (err) {
      console.error("Error checking documents:", err);
      return { hasDocuments: false, count: 0 };
    }
  };

  const loadTeams = async () => {
    try {
      const result = await Parse.Cloud.run("getteams", { active: true });
      if (result) {
        const teams = JSON.parse(JSON.stringify(result));
        const filtered = teams.filter((t) => t.Name !== "All Users");
        setTeamList(filtered);
      }
    } catch (err) {
      console.error("Error loading teams:", err);
    }
  };

  const loadUsersInTeams = async (teamIds) => {
    try {
      if (!teamIds || teamIds.length === 0) {
        setUserList([]);
        return;
      }

      const result = await Parse.Cloud.run("getuserlistbyteams", {
        teamIds: teamIds
      });

      if (result) {
        const users = JSON.parse(JSON.stringify(result));
        // Filter out the current user
        const filtered = users.filter((u) => u.objectId !== user.objectId);
        setUserList(filtered);
      }
    } catch (err) {
      console.error("Error loading users:", err);
    }
  };

  const loadAllUsers = async () => {
    try {
      const result = await Parse.Cloud.run("getallusers", {});
      if (result) {
        const users = JSON.parse(JSON.stringify(result));
        setAllUsers(users);
      }
    } catch (err) {
      console.error("Error loading all users:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleTeamChange = (teamId) => {
    setFormData((prev) => {
      const teams = prev.teams.includes(teamId)
        ? prev.teams.filter((id) => id !== teamId)
        : [...prev.teams, teamId];
      return { ...prev, teams };
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    if (!formData.role) {
      setError("Role is required");
      return;
    }

    // Check if teams are being removed
    const removedTeams = originalTeams.filter(
      (id) => !formData.teams.includes(id)
    );

    if (removedTeams.length > 0) {
      // Check if user has documents in removed teams
      const hasDocsInRemovedTeams = await checkDocumentsInTeams(removedTeams);

      if (hasDocsInRemovedTeams) {
        // Show delegation modal
        await loadUsersInTeams(formData.teams);
        setShowDelegationModal(true);
        return;
      }
    }

    // Submit without delegation
    await submitUpdate();
  };

  const checkDocumentsInTeams = async (teamIds) => {
    try {
      const userId = user.UserId?.id || user.UserId?.objectId;
      if (!userId) return false;

      const teamPointers = teamIds.map((teamId) => ({
        __type: "Pointer",
        className: "contracts_Teams",
        objectId: teamId
      }));

      const docQuery = new Parse.Query("contracts_Document");
      docQuery.containedIn("TeamId", teamPointers);
      docQuery.equalTo("CreatedBy", {
        __type: "Pointer",
        className: "_User",
        objectId: userId
      });
      docQuery.limit(1);

      const count = await docQuery.count();
      return count > 0;
    } catch (err) {
      console.error("Error checking documents in teams:", err);
      return false;
    }
  };

  const submitUpdate = async (documentDelegation = null) => {
    setIsLoading(true);
    setError("");

    try {
      const updates = {
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        teams: formData.teams
      };

      // Only include email if user doesn't have documents
      if (!hasDocuments && formData.email !== user.Email) {
        updates.email = formData.email;
      }

      // Only include password if it was changed
      if (formData.password) {
        updates.password = formData.password;
      }

      // Include document delegation if provided
      if (documentDelegation) {
        updates.documentDelegation = documentDelegation;
      }

      const result = await Parse.Cloud.run("edituser", {
        userId: user.objectId,
        updates: updates
      });

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.message);
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message || "Failed to update user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelegationSubmit = () => {
    if (!delegationAction) {
      setError("Please select a delegation option");
      return;
    }

    if (
      (delegationAction === "delegateToUser" && !delegationTarget) ||
      (delegationAction === "assignToTeam" && !delegationTarget)
    ) {
      setError("Please select a target for delegation");
      return;
    }

    const documentDelegation = {
      action: delegationAction,
      targetUserId:
        delegationAction === "delegateToUser" ? delegationTarget : null,
      targetTeamId:
        delegationAction === "assignToTeam" ? delegationTarget : null
    };

    setShowDelegationModal(false);
    submitUpdate(documentDelegation);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      password: "",
      role: "",
      teams: []
    });
    setOriginalTeams([]);
    setError("");
    setShowDelegationModal(false);
    setDelegationAction("");
    setDelegationTarget("");
    setHasDocuments(false);
    setShowDeleteModal(false);
    onClose();
  };

  const handleDeleteUser = async (deleteOptions) => {
    try {
      const userId = user.UserId?.objectId || user.UserId?.id;
      const result = await Parse.Cloud.run("deleteorcloseuser", {
        userId: userId,
        action: deleteOptions.action,
        transferDocuments: deleteOptions.transferDocuments,
        targetUserId: deleteOptions.targetUserId
      });

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.message);
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error deleting/closing user:", err);
      throw err;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalUi
        isOpen={isOpen}
        title="Edit User"
        handleClose={handleClose}
        showClose={!isLoading}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="op-input op-input-bordered w-full"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="op-input op-input-bordered w-full"
              disabled={isLoading || hasDocuments}
              title={
                hasDocuments
                  ? "Cannot change email for users with assigned documents"
                  : ""
              }
            />
            {hasDocuments && (
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed for users with assigned documents
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="op-input op-input-bordered w-full"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              New Password (leave empty to keep current)
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="op-input op-input-bordered w-full"
              disabled={isLoading}
              placeholder="Enter new password or leave empty"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="op-select op-select-bordered w-full"
              disabled={isLoading}
            >
              <option value="">Select Role</option>
              {allRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Teams</label>
            <div className="border rounded p-3 max-h-40 overflow-y-auto">
              {teamList.length > 0 ? (
                teamList.map((team) => (
                  <label
                    key={team.objectId}
                    className="flex items-center mb-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.teams.includes(team.objectId)}
                      onChange={() => handleTeamChange(team.objectId)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="text-sm">{team.Name}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500">No teams available</p>
              )}
            </div>
          </div>

          <div className="flex justify-between gap-2 pt-4">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="op-btn op-btn-error"
              disabled={isLoading}
            >
              Delete/Close User
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="op-btn op-btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="op-btn op-btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </ModalUi>

      {/* Document Delegation Modal */}
      {showDelegationModal && (
        <Modal>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-auto">
            <h3 className="text-lg font-semibold mb-4">
              Document Delegation Required
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This user has documents assigned in teams they are being removed
              from. Please choose how to handle these documents:
            </p>

            <div className="space-y-3 mb-4">
              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="delegationAction"
                  value="delegateToUser"
                  checked={delegationAction === "delegateToUser"}
                  onChange={(e) => {
                    setDelegationAction(e.target.value);
                    setDelegationTarget("");
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Delegate to another user
                  </div>
                  <div className="text-xs text-base-content/70">
                    Transfer documents to another user in the current team
                  </div>
                </div>
              </label>

              {delegationAction === "delegateToUser" && (
                <select
                  value={delegationTarget}
                  onChange={(e) => setDelegationTarget(e.target.value)}
                  className="op-select op-select-bordered w-full ml-8"
                >
                  <option value="">Select user</option>
                  {userList.map((u) => (
                    <option key={u.objectId} value={u.UserId?.objectId}>
                      {u.Name} ({u.Email})
                    </option>
                  ))}
                </select>
              )}

              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="delegationAction"
                  value="assignToTeam"
                  checked={delegationAction === "assignToTeam"}
                  onChange={(e) => {
                    setDelegationAction(e.target.value);
                    setDelegationTarget("");
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Assign to another team
                  </div>
                  <div className="text-xs text-base-content/70">
                    Move documents to a different team
                  </div>
                </div>
              </label>

              {delegationAction === "assignToTeam" && (
                <select
                  value={delegationTarget}
                  onChange={(e) => setDelegationTarget(e.target.value)}
                  className="op-select op-select-bordered w-full ml-8"
                >
                  <option value="">Select team</option>
                  {teamList
                    .filter((t) => formData.teams.includes(t.objectId))
                    .map((team) => (
                      <option key={team.objectId} value={team.objectId}>
                        {team.Name}
                      </option>
                    ))}
                </select>
              )}
            </div>

            {error && (
              <div className="alert alert-error mb-4">
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDelegationModal(false)}
                className="op-btn op-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelegationSubmit}
                className="op-btn op-btn-primary"
              >
                Continue
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete/Close User Modal */}
      {showDeleteModal && (
        <DeleteUserConfirmation
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          user={user}
          hasDocuments={hasDocuments}
          documentCount={documentCount}
          userList={allUsers}
          onConfirm={handleDeleteUser}
        />
      )}
    </>
  );
};

export default EditUser;
